import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Handshake } from 'lucide-react';
import { billingApi, ClientCommissionOwedDto, formatCurrency, handleApiError } from '../../services/paymentsService';

interface AgentCommissionOwedProps {
  clientId: number;
}

// P9a — תצוגת קריאה-בלבד של העמלות החבות לסוכן/ארגון של הלקוח (מגודר CanViewClientFinance בשרת).
// שני מצבים בלבד: עמלת חתימה (חבה תמיד) ועמלת אישור (מוצגת אך ורק כשהקצבה אושרה). אין תג "ממתין".

const Container = styled.div`
  padding: 20px 0;
  margin-bottom: 4px;
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const PartyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const PartyCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 14px 16px;
  background: var(--surface-color);
`;

const PartyHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color-light);
`;

const PartyRole = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color);
`;

const PartyName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: left;
  direction: rtl;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
`;

const Label = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const Value = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  direction: ltr;
`;

const PartyTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-color-light);
`;

const PartyTotalLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;

const PartyTotalValue = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #16a34a;
  direction: ltr;
`;

const GrandTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
`;

const GrandTotalLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const GrandTotalValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
  direction: ltr;
`;

const NoteText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 6px;
`;

interface PartyProps {
  role: string;
  name?: string | null;
  signingAmount?: number | null;
  signingOwed?: number | null;
  approvalPercentage?: number | null;
  approvalOwed?: number | null;
  approvalBaseNet?: number | null;
  totalOwed: number;
}

const PartySection: React.FC<PartyProps> = ({
  role, name, signingAmount, signingOwed, approvalPercentage, approvalOwed, approvalBaseNet, totalOwed,
}) => {
  const showSigning = signingAmount != null; // עמלת חתימה הוגדרה
  const showApproval = approvalOwed != null;  // קיימת אך ורק כשהקצבה אושרה

  return (
    <PartyCard>
      <PartyHeader>
        <PartyRole>{role}</PartyRole>
        <PartyName>{name || '—'}</PartyName>
      </PartyHeader>

      {showSigning && (
        <Row>
          <Label>עמלת חתימה</Label>
          <Value>{formatCurrency(signingOwed ?? 0)}</Value>
        </Row>
      )}

      {showApproval && (
        <Row>
          <Label>עמלת אישור ({approvalPercentage}% מ-{formatCurrency(approvalBaseNet ?? 0)})</Label>
          <Value>{formatCurrency(approvalOwed ?? 0)}</Value>
        </Row>
      )}

      {!showSigning && !showApproval && (
        <NoteText>לא הוגדרו ערכי עמלה לצד זה</NoteText>
      )}

      <PartyTotalRow>
        <PartyTotalLabel>סה"כ חב</PartyTotalLabel>
        <PartyTotalValue>{formatCurrency(totalOwed)}</PartyTotalValue>
      </PartyTotalRow>
    </PartyCard>
  );
};

const AgentCommissionOwed: React.FC<AgentCommissionOwedProps> = ({ clientId }) => {
  const [data, setData] = useState<ClientCommissionOwedDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const result = await billingApi.getCommissionOwed(clientId);
      setData(result);
    } catch (err) {
      setError(handleApiError(err));
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error || !data) return null;

  const hasAgent = data.agentSourceId != null;
  const hasOrganization = data.organizationSourceId != null;

  // אין שיוך כלל — אין מה להציג.
  if (!hasAgent && !hasOrganization) return null;

  return (
    <Container>
      <Title>
        <Handshake size={18} />
        עמלות סוכן / ארגון מפנה
      </Title>

      <PartyGrid>
        {hasAgent && (
          <PartySection
            role="סוכן מפנה"
            name={data.agentSourceName}
            signingAmount={data.agentSigningCommissionAmount}
            signingOwed={data.agentSigningOwed}
            approvalPercentage={data.agentApprovalCommissionPercentage}
            approvalOwed={data.agentApprovalOwed}
            approvalBaseNet={data.approvalBaseNet}
            totalOwed={data.agentTotalOwed}
          />
        )}
        {hasOrganization && (
          <PartySection
            role="ארגון מפנה"
            name={data.organizationSourceName}
            signingAmount={data.organizationSigningCommissionAmount}
            signingOwed={data.organizationSigningOwed}
            approvalPercentage={data.organizationApprovalCommissionPercentage}
            approvalOwed={data.organizationApprovalOwed}
            approvalBaseNet={data.approvalBaseNet}
            totalOwed={data.organizationTotalOwed}
          />
        )}
      </PartyGrid>

      {hasAgent && hasOrganization && (
        <GrandTotalRow>
          <GrandTotalLabel>סה"כ עמלות מצטבר (סוכן + ארגון)</GrandTotalLabel>
          <GrandTotalValue>{formatCurrency(data.totalOwed)}</GrandTotalValue>
        </GrandTotalRow>
      )}
    </Container>
  );
};

export default AgentCommissionOwed;
